// src/pages/QAResultsPage.js — widgetboss-only window showing chatbot test-suite results
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, MinusCircle, AlertTriangle, XCircle, ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';

// Conversation ids in the suite → readable topic names.
const TOPICS = {
  'hours-contact': 'Office hours & contact', 'maintenance': 'Maintenance & emergencies', 'waitlist-then-apply': 'Waiting list, then applying',
  'apply-then-waitlist': 'Applying, then waiting list', 'income-recert': 'Income change & recertification', 'voucher-holder': 'Voucher holder questions',
  'staff': 'Staff & who to contact', 'board': 'Board meetings', 'pets-rules': 'Pets, smoking & household rules', 'income-limits': 'Income limits',
  'properties': 'Properties & addresses', 'spanish': 'Spanish-language questions', 'topic-switching': 'Topic switching', 'rent-lease': 'Rent & lease',
  'ashley-workorder': 'Work orders (prospect complaint)', 'status-variants': 'Application status', 'forms': 'Forms & documents', 'portability': 'Portability / transfers',
};
const topicOf = (name) => { const id = name.includes(':') ? name.split(':').slice(1).join(':') : name; return TOPICS[id] || id; };

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8787'
  : 'https://housing-external.onrender.com';
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || '';

async function adminFetch(path) {
  const url = new URL(`${API_URL}/admin/data${path}`);
  url.searchParams.set('key', ADMIN_API_KEY);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Admin API error: ${res.status}`);
  return res.json();
}

const VERDICT = {
  correct:    { label: 'Correct',        color: 'text-green-700 bg-green-50 border-green-200',  Icon: CheckCircle2 },
  honest_gap: { label: 'Not on site',    color: 'text-gray-700 bg-gray-50 border-gray-200',     Icon: MinusCircle },
  unhelpful:  { label: 'Missed',         color: 'text-amber-700 bg-amber-50 border-amber-200',  Icon: AlertTriangle },
  wrong:      { label: 'Wrong',          color: 'text-red-700 bg-red-50 border-red-200',        Icon: XCircle },
  error:      { label: 'Not graded',     color: 'text-gray-500 bg-gray-50 border-gray-200',     Icon: MinusCircle },
};
const ORDER = ['correct', 'honest_gap', 'unhelpful', 'wrong', 'error'];

const Badge = ({ verdict }) => {
  const v = VERDICT[verdict] || VERDICT.error;
  const Icon = v.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${v.color}`}>
      <Icon className="h-3.5 w-3.5" /> {v.label}
    </span>
  );
};

const ScoreBar = ({ s }) => {
  const n = s.n || 1;
  const seg = (k, cls) => <div className={cls} style={{ width: `${(s[k] / n) * 100}%` }} title={`${VERDICT[k].label}: ${s[k]}`} />;
  return (
    <div className="flex h-2.5 w-full rounded overflow-hidden bg-gray-100">
      {seg('correct', 'bg-green-500')}{seg('honest_gap', 'bg-gray-400')}{seg('unhelpful', 'bg-amber-400')}{seg('wrong', 'bg-red-500')}
    </div>
  );
};

const orgLabel = (slug) => slug.charAt(0).toUpperCase() + slug.slice(1);
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const AnswerKey = ({ k }) => {
  const [show, setShow] = useState(false);
  if (!k || ((k.facts || []).length === 0 && (k.snippets || []).length === 0)) return null;
  return (
    <div className="text-xs">
      <button type="button" onClick={() => setShow(v => !v)} className="font-semibold text-amber-800">Answer key (what the site says) {show ? '−' : '+'}</button>
      {show && (
        <div className="mt-1 rounded border border-amber-200 bg-amber-50 p-2 space-y-2 text-gray-800">
          {(k.facts || []).length > 0 && (
            <div>
              <div className="font-semibold text-amber-900">Verified structured facts</div>
              {k.facts.map((f, i) => <p key={i} className="whitespace-pre-wrap break-words">{f}</p>)}
            </div>
          )}
          {(k.snippets || []).length > 0 && (
            <div className="space-y-1">
              <div className="font-semibold text-amber-900">Page excerpts</div>
              {k.snippets.map((sn, i) => (
                <p key={i} className="break-words"><a href={sn.url} target="_blank" rel="noreferrer" className="text-blue-700 underline">{sn.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 80)}</a> — {String(sn.text).replace(/<\/?b>/g, '')}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Conversation = ({ name, turns, filter, forceOpen }) => {
  const [openState, setOpen] = useState(false);
  const open = forceOpen === null || forceOpen === undefined ? openState : forceOpen;
  const counts = useMemo(() => ORDER.reduce((acc, k) => ({ ...acc, [k]: turns.filter(t => t.verdict === k).length }), {}), [turns]);
  const shown = filter === 'all' ? turns : turns.filter(t => t.verdict === filter);
  if (filter !== 'all' && shown.length === 0) return null;
  const title = topicOf(name);
  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 font-medium text-gray-800">
          {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          {title}
          <span className="text-xs text-gray-400 font-normal">{turns.length} turns</span>
        </span>
        <span className="flex items-center gap-2 text-xs">
          {counts.wrong > 0 && <span className="text-red-700">{counts.wrong} wrong</span>}
          {counts.unhelpful > 0 && <span className="text-amber-700">{counts.unhelpful} missed</span>}
          {counts.honest_gap > 0 && <span className="text-gray-500">{counts.honest_gap} not on site</span>}
          <span className="text-green-700">{counts.correct} correct</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {shown.map(t => (
            <div key={t.i} className="px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium text-gray-900">Turn {t.i + 1}: <span className="font-normal">“{t.q}”</span></p>
                <Badge verdict={t.verdict} />
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-3">{t.a}</p>
              {t.reason && (
                <p className={`text-xs ${t.verdict === 'correct' ? 'text-gray-500' : 'text-gray-700'}`}><span className="font-medium">Grader:</span> {t.reason}</p>
              )}
              <AnswerKey k={t.key} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QAResultsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [org, setOrg] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expandAll, setExpandAll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Standalone route gates itself; the dashboard tab is already widgetboss-only.
    const ok = embedded || (localStorage.getItem('isAuthenticated') && localStorage.getItem('currentUser') === 'widgetboss');
    if (!ok) { navigate('/login'); return; }
    adminFetch('/qa-runs').then(d => {
      setRuns(d.runs || []);
      if (d.runs?.length) setRunId(d.runs[0].id);
      else setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, [navigate, embedded]);

  useEffect(() => {
    if (!runId) return;
    setLoading(true); setRun(null); setOrg(null);
    adminFetch(`/qa-runs/${runId}`).then(d => { setRun(d); setLoading(false); }).catch(e => { setError(e.message); setLoading(false); });
  }, [runId]);

  const byOrg = run?.summary?.byOrg || {};
  const orgs = Object.keys(byOrg).sort((a, b) => byOrg[a].score - byOrg[b].score);
  const orgTurns = useMemo(() => (run?.turns || []).filter(t => t.org === org), [run, org]);
  const convos = useMemo(() => {
    const m = new Map();
    const q = query.trim().toLowerCase();
    for (const t of orgTurns) {
      if (q && !(`${t.q} ${t.a} ${t.reason || ''} ${topicOf(t.name)}`.toLowerCase().includes(q))) continue;
      if (!m.has(t.name)) m.set(t.name, []); m.get(t.name).push(t);
    }
    return [...m.entries()];
  }, [orgTurns, query]);
  const pickOrg = (o) => { setOrg(o); setFilter('all'); setQuery(''); setExpandAll(null); };

  return (
    <div className={embedded ? 'text-gray-900' : 'min-h-screen bg-gray-50 text-gray-900'}>
      <header className={`${embedded ? 'bg-white border border-gray-200 rounded-lg mb-4' : 'bg-white border-b border-gray-200'} px-6 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          {org ? (
            <button onClick={() => setOrg(null)} className="text-sm text-blue-700 flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> All orgs</button>
          ) : null}
          <h1 className="text-lg font-semibold">Chatbot QA results{org ? ` · ${orgLabel(org)}` : ''}</h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {runs.length > 0 && (
            <select value={runId || ''} onChange={e => setRunId(Number(e.target.value))} className="border border-gray-300 rounded px-2 py-1 bg-white">
              {runs.map(r => <option key={r.id} value={r.id}>{r.label} · {fmtWhen(r.created_at)}</option>)}
            </select>
          )}
          <button onClick={() => window.location.reload()} className="p-1.5 rounded hover:bg-gray-100" title="Reload"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </header>

      <main className={embedded ? 'space-y-6' : 'max-w-6xl mx-auto px-6 py-6 space-y-6'}>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>}
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && !run && !error && <p className="text-sm text-gray-500">No test runs stored yet.</p>}

        {run && !org && (
          <>
            <section className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-500">{run.label}{run.build ? ` · build ${run.build}` : ''} · {fmtWhen(run.created_at)}</p>
                  <p className="text-3xl font-semibold mt-1">{run.summary.total.score}% <span className="text-base font-normal text-gray-500">correct or honestly not on site</span></p>
                </div>
                <div className="flex gap-4 text-sm">
                  {ORDER.filter(k => k !== 'error' || run.summary.total.error).map(k => (
                    <span key={k} className="flex items-center gap-1"><Badge verdict={k} /> {run.summary.total[k]}</span>
                  ))}
                  <span className="text-gray-500">{run.summary.total.n} turns</span>
                </div>
              </div>
              <div className="mt-4"><ScoreBar s={run.summary.total} /></div>
            </section>

            <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">Org</th>
                    <th className="px-4 py-2 font-medium">Score</th>
                    <th className="px-4 py-2 font-medium w-1/3">Breakdown</th>
                    <th className="px-4 py-2 font-medium text-right">Correct</th>
                    <th className="px-4 py-2 font-medium text-right">Not on site</th>
                    <th className="px-4 py-2 font-medium text-right">Missed</th>
                    <th className="px-4 py-2 font-medium text-right">Wrong</th>
                    <th className="px-4 py-2 font-medium text-right">Turns</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orgs.map(o => {
                    const s = byOrg[o];
                    return (
                      <tr key={o} onClick={() => pickOrg(o)} className="hover:bg-blue-50 cursor-pointer">
                        <td className="px-4 py-2 font-medium text-blue-700">{orgLabel(o)}</td>
                        <td className="px-4 py-2 font-semibold">{s.score}%</td>
                        <td className="px-4 py-2"><ScoreBar s={s} /></td>
                        <td className="px-4 py-2 text-right text-green-700">{s.correct}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{s.honest_gap}</td>
                        <td className="px-4 py-2 text-right text-amber-700">{s.unhelpful}</td>
                        <td className="px-4 py-2 text-right text-red-700">{s.wrong}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{s.n}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
            <p className="text-xs text-gray-500">Each turn is graded against the authority's own website: <b>Correct</b> = every specific claim is on the site; <b>Not on site</b> = the bot said so and it is true; <b>Missed</b> = the bot said not on site but it is; <b>Wrong</b> = a claim the site does not support.</p>
          </>
        )}

        {run && org && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {orgs.map(o => (
                <button key={o} onClick={() => pickOrg(o)} className={`px-3 py-1 rounded-full border text-sm ${o === org ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {orgLabel(o)} <span className={o === org ? 'text-blue-100' : 'text-gray-400'}>{byOrg[o].score}%</span>
                </button>
              ))}
            </div>
            <section className="bg-white border border-gray-200 rounded-lg p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-2xl font-semibold">{byOrg[org].score}%</p>
                <p className="text-sm text-gray-500">{byOrg[org].n} turns across {byOrg[org].conversations} conversations</p>
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-sm">
                {['all', 'wrong', 'unhelpful', 'honest_gap', 'correct'].map(k => (
                  <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded ${filter === k ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600'}`}>
                    {k === 'all' ? 'All' : VERDICT[k].label}{k !== 'all' ? ` (${byOrg[org][k]})` : ''}
                  </button>
                ))}
              </div>
              <div className="w-full"><ScoreBar s={byOrg[org]} /></div>
              <div className="w-full flex flex-wrap items-center gap-2 text-sm">
                <label className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 bg-white flex-1 min-w-[220px]">
                  <Search className="h-4 w-4 text-gray-400" />
                  <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search questions, answers, grader notes…" className="flex-1 outline-none text-sm" />
                </label>
                <button onClick={() => setExpandAll(true)} className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Expand all</button>
                <button onClick={() => setExpandAll(false)} className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Collapse all</button>
              </div>
            </section>
            <div className="space-y-2">
              {convos.length === 0 && <p className="text-sm text-gray-500">No turns match.</p>}
              {convos.map(([name, turns]) => <Conversation key={`${name}-${expandAll}`} name={name} turns={turns} filter={filter} forceOpen={expandAll} />)}
            </div>
            <p className="text-xs text-gray-500">Each turn shows the question, the bot's answer, the verdict, the grader's note, and an answer key: the verified structured facts and the page excerpts from this authority's own website that the answer was judged against. Hand-reviewed turns say so in the grader note.</p>
          </>
        )}
      </main>
    </div>
  );
};

export default QAResultsPage;
